import { Alert, FormSurface, Input, Select } from "@vjal/ui";
import { useState } from "react";
import { CapabilityButton } from "../components/AccessState";
import type { ActionCapabilityDecision } from "../domain/capabilities";
import { ApiClientError } from "../services/api-client";

/**
 * Shared live-mode mutation form/action module.
 *
 * Live lanes render only in live data mode; mock/evidence mode keeps the
 * deterministic contract fixtures the e2e suite asserts. Submits stay
 * capability-gated (CapabilityButton), call the live services with UUID
 * idempotency keys, render backend validation errors with their CO-
 * references, and let the owning hooks refresh the affected lists on success.
 */

export interface LiveFieldOption {
  value: string;
  label: string;
}

export interface LiveField {
  name: string;
  label: string;
  hint?: string;
  type?: "text" | "email" | "date" | "time" | "number";
  options?: LiveFieldOption[];
  optionsEmptyHint?: string;
  required?: boolean;
}

export type LiveSubmitResult = { id?: string };
export type LiveFormOutcome =
  | { kind: "success"; id?: string }
  | { kind: "error"; message: string; reference?: string }
  | null;
export type LiveFieldValidator = (values: Record<string, string>) => Record<string, string>;

export function LiveFormModule({
  title,
  subtitle,
  entityLabel,
  fields,
  decision,
  submitLabel,
  successVerb = "Created",
  onSubmit,
  validate,
  span = 8
}: {
  title: string;
  subtitle: string;
  entityLabel: string;
  fields: LiveField[];
  decision: ActionCapabilityDecision;
  submitLabel: string;
  successVerb?: string;
  onSubmit: (values: Record<string, string>) => Promise<LiveSubmitResult>;
  validate?: LiveFieldValidator;
  span?: 4 | 6 | 8 | 12;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<LiveFormOutcome>(null);
  const [pending, setPending] = useState(false);

  function valueOf(field: LiveField) {
    return values[field.name] ?? "";
  }

  async function submit() {
    const submittedValues = Object.fromEntries(fields.map((field) => [field.name, valueOf(field).trim()]));
    const errors: Record<string, string> = {};
    for (const field of fields) {
      if (field.required && !submittedValues[field.name]) {
        errors[field.name] = `${field.label} is required before ${submitVerbNoun(successVerb)}.`;
      }
    }
    if (Object.keys(errors).length === 0 && validate) Object.assign(errors, validate(submittedValues));
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setOutcome(null);
      return;
    }
    setPending(true);
    setOutcome(null);
    try {
      const created = await onSubmit(Object.fromEntries(fields.map((field) => [field.name, valueOf(field).trim()])));
      setOutcome({ kind: "success", id: created.id });
    } catch (error) {
      setOutcome(toErrorOutcome(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className={span === 12 ? "col-span-12" : span === 6 ? "col-span-6" : span === 4 ? "col-span-4" : "col-span-8"}>
      {/* F3: real @vjal/ui FormSurface for Form-1 live create/edit lanes. */}
      <FormSurface
        title={title}
        hideSubmit
        error={outcome?.kind === "error" ? `${outcome.message}${outcome.reference ? ` (ref ${outcome.reference})` : ""}` : undefined}
      >
        <p className="m-0 text-sm text-ink-muted">{subtitle}</p>
        {fields.map((field) => (
          <LiveFieldControl
            key={field.name}
            field={field}
            value={valueOf(field)}
            error={fieldErrors[field.name]}
            disabled={!decision.allowed || pending}
            onChange={(nextValue) => setValues((previous) => ({ ...previous, [field.name]: nextValue }))}
          />
        ))}
        {outcome?.kind === "success" ? (
          <Alert variant="success">
            {`${successVerb} ${entityLabel}${outcome.id ? ` (id ${outcome.id})` : ""}. Live lists refresh automatically.`}
          </Alert>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <CapabilityButton decision={decision} loading={pending} onClick={() => void submit()}>
            {submitLabel}
          </CapabilityButton>
        </div>
      </FormSurface>
    </div>
  );
}

/**
 * Two-step live lifecycle action (complete / deactivate / upload operations):
 * the first click arms the control with explicit confirmation copy, the second
 * click runs the mutation. Outcomes render with their CO- references.
 */
export function LiveActionControl({
  label,
  confirmLabel,
  entityLabel,
  successVerb,
  decision,
  onConfirm
}: {
  label: string;
  confirmLabel: string;
  entityLabel: string;
  successVerb: string;
  decision: ActionCapabilityDecision;
  onConfirm: () => Promise<LiveSubmitResult>;
}) {
  const [armed, setArmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<LiveFormOutcome>(null);

  async function activate() {
    if (!armed) {
      setArmed(true);
      setOutcome(null);
      return;
    }
    setPending(true);
    setOutcome(null);
    try {
      const result = await onConfirm();
      setOutcome({ kind: "success", id: result.id });
    } catch (error) {
      setOutcome(toErrorOutcome(error));
    } finally {
      setPending(false);
      setArmed(false);
    }
  }

  return (
    <div className="grid gap-2">
      <CapabilityButton decision={decision} loading={pending} onClick={() => void activate()}>
        {armed ? confirmLabel : label}
      </CapabilityButton>
      {armed && !pending ? <Alert variant="warning">{`Click "${confirmLabel}" to run this live action.`}</Alert> : null}
      {outcome?.kind === "success" ? (
        <Alert variant="success">{`${successVerb} ${entityLabel}${outcome.id ? ` (id ${outcome.id})` : ""}.`}</Alert>
      ) : null}
      {outcome?.kind === "error" ? (
        <Alert variant="error">{`${outcome.message}${outcome.reference ? ` (ref ${outcome.reference})` : ""}`}</Alert>
      ) : null}
    </div>
  );
}

function toErrorOutcome(error: unknown): LiveFormOutcome {
  if (error instanceof ApiClientError) {
    return { kind: "error", message: error.message, reference: error.reference ?? undefined };
  }
  return { kind: "error", message: "The live request failed unexpectedly." };
}

function submitVerbNoun(successVerb: string) {
  return successVerb === "Updated" ? "save" : successVerb === "Rescheduled" ? "reschedule" : "create";
}

function LiveFieldControl({
  field,
  value,
  error,
  disabled,
  onChange
}: {
  field: LiveField;
  value: string;
  error?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  if (field.options) {
    const emptyOptions = field.options.length === 0;
    return (
      <Select
        label={field.label}
        hint={emptyOptions ? (field.optionsEmptyHint ?? "No live records are available to select yet.") : field.hint}
        error={error}
        value={value}
        disabled={disabled || emptyOptions}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Select…</option>
        {field.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  }

  return (
    <Input
      label={field.label}
      hint={field.hint}
      error={error}
      type={field.type ?? "text"}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export function toOptions(records: Array<{ id: string; label: string }>): LiveFieldOption[] {
  return records.map((record) => ({ value: record.id, label: `${record.label} (id ${record.id})` }));
}

export function userOptions(users: Array<{ id: string; name?: string; email?: string; role?: string }>): LiveFieldOption[] {
  return toOptions(
    users.map((user) => ({
      id: user.id,
      label: `${user.name || user.email || "user"}${user.role ? ` — ${user.role}` : ""}`
    }))
  );
}

/**
 * PATCH lanes send only the fields a human actually filled in; blank inputs
 * mean "keep the current value" so an edit can never blank fields by accident.
 */
export function compactPatchBody(values: Record<string, string>, exclude: string[] = []): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values).filter(([key, value]) => !exclude.includes(key) && value.trim() !== "")
  );
}
